/**
 * Location Utilities
 * A collection of utility classes for handling location, mapping, and shipping functionality
 */

class LocationValidator {
  /**
   * Validates if a user address is valid for the current fulfillment type
   * @param {Object} address - The address to validate
   * @param {string} fulfillmentType - The current fulfillment type
   * @returns {boolean} - Whether the address is valid
   */
  static validateUserAddress(address, fulfillmentType) {
    if (address.is_store && fulfillmentType !== "pickup") {
      return false
    }

    const validationFields = ["address1", "city", "latitude", "longitude", "postalCode", "province"]
    let isValid = true

    validationFields.forEach((field) => {
      if (!isValid) {
        return
      }

      if (fulfillmentType === "pickup") {
        if (field === "address1" || field === "postalCode") {
          return
        }
      }

      isValid = Boolean(address[field])
    })

    return isValid
  }

  /**
   * Validates a parsed address for completeness and correctness
   * @param {Object} parsedAddress - The parsed address to validate
   * @param {string} fulfillmentType - The current fulfillment type
   * @returns {Object} - Validation result with isValid flag and message
   */
  static validateParsedAddress(parsedAddress, fulfillmentType) {
    const { address1, lat, lng, streetNumber } = parsedAddress

    if (lat == 0 || lng == 0) {
      return {
        isValid: false,
        message: "",
      }
    }

    if (fulfillmentType !== "pickup" && (!address1 || !streetNumber)) {
      return {
        isValid: false,
        message: "Street number or name is missing, please enter a more detailed address.",
      }
    }

    return {
      isValid: true,
      message: "",
    }
  }

  /**
   * Checks if two places match by ID or address
   * @param {Object} place1 - First place to compare
   * @param {Object} place2 - Second place to compare
   * @returns {boolean} - Whether the places match
   */
  static isPlaceMatch(place1, place2) {
    if (!place1 || !place2) {
      return false
    }

    const isPlaceIdMatch = Boolean(place1.place_id && place1.place_id === place2.place_id)
    const isPlaceAddressMatch = Boolean(place1.formatted_address === place2.formatted_address)

    return Boolean(isPlaceIdMatch || isPlaceAddressMatch)
  }

  /**
   * Formats a place object, optionally ignoring address1 components
   * @param {Object} place - The place to format
   * @param {boolean} ignoreAddress1 - Whether to ignore address1 components
   * @returns {Object} - The formatted place
   */
  static formatPlace(place, ignoreAddress1) {
    if (!ignoreAddress1) {
      return place
    }

    const address_components = place.address_components.filter((component) => {
      const isStreetNumber = component.types[0] === "street_number"
      const isRoute = component.types[0] === "route"

      return !isStreetNumber && !isRoute
    })

    return {
      ...place,
      address_components,
    }
  }
}

class MapManager {
  /**
   * Initialize a map manager
   * @param {HTMLElement} mapElement - The DOM element for the map
   */
  constructor(mapElement) {
    this.mapElement = mapElement
    this.map = null
    this.marker = null
    this.isRadarInit = false
  }

  /**
   * Clear the current map instance
   * @returns {HTMLElement} - The new map element
   */
  clearMap() {
    if (!this.mapElement || !this.map) {
      return this.mapElement
    }

    const newMapElement = document.createElement("div")
    newMapElement.classList.add("location-modal__map")
    this.map = null
    this.mapElement.after(newMapElement)
    this.mapElement.remove()
    this.mapElement = newMapElement
    return this.mapElement
  }

  /**
   * Set the map location using the appropriate map provider
   * @param {Object} position - The position to set
   * @returns {Promise<void>}
   */
  async setMapLocation(position) {
    if (!this.mapElement) {
      return
    }

    if (window.gopuffShopify.settings.map_source === "google") {
      await this.setMapLocationViaGoogle(position)
      return
    }

    this.setMapLocationViaRadar(position)
  }

  /**
   * Set the map location using Google Maps
   * @param {Object} position - The position to set
   * @returns {Promise<void>}
   */
  async setMapLocationViaGoogle(position) {
    if (this.map) {
      this.map.setCenter(position)
      this.marker.setPosition(position)
      return
    }

    const { Map } = await google.maps.importLibrary("maps")
    this.map = new Map(this.mapElement, {
      center: position,
      zoom: 19,
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: false,
      zoomControl: false,
      gestureHandling: "none",
      keyboardShortcuts: false,
    })
    this.marker = new google.maps.Marker({
      position: position,
      map: this.map,
    })
  }

  /**
   * Set the map location using Radar
   * @param {Object} position - The position to set
   */
  setMapLocationViaRadar(position) {
    if (!window.Radar) {
      return
    }

    if (!this.isRadarInit) {
      window.Radar.initialize(window.gopuffShopify.settings.radar_api_key)
      this.isRadarInit = true
    }

    const center = [position.lng, position.lat]
    this.map = window.Radar.ui.map({
      center,
      zoom: 18,
      container: this.mapElement,
      style: "radar-default-v1",
      language: "en-US",
      interactive: false,
    })

    this.marker = window.Radar.ui
      .marker({
        color: "var(--bevmo-red)",
        width: 26,
        height: 37,
      })
      .setLngLat(center)
      .addTo(this.map)
  }
}

class ShippingManager {
  /**
   * Creates a loading content placeholder
   * @param {number} placeholders_count - Number of placeholder elements to create
   * @returns {DocumentFragment} - Fragment containing placeholder elements
   */
  static getLoadingContent(placeholders_count = 5) {
    const loading_content = new DocumentFragment()

    for (let i = 0; i < placeholders_count; i++) {
      const placeholder = document.createElement("div")
      placeholder.classList.add("skeleton-box")
      loading_content.appendChild(placeholder)
    }

    return loading_content
  }

  /**
   * Toggles the loading state for pickup locations
   * @param {HTMLElement} pickupLocationsElement - The pickup locations container
   * @param {boolean} state - Whether to show loading state
   */
  static toggleLoadingStateForPickupLocations(pickupLocationsElement, state) {
    switch (state) {
      case true:
        pickupLocationsElement.classList.add("loading")
        pickupLocationsElement.appendChild(ShippingManager.getLoadingContent())
        break
      case false:
        pickupLocationsElement.classList.remove("loading")
        pickupLocationsElement.innerHTML = ""
        break
    }
  }

  /**
   * Gets the selected pickup location ID from available locations
   * @param {Object} deliveryZoneResponse - The delivery zone response
   * @param {string|null} selectedPickupLocationId - Currently selected pickup location ID
   * @returns {string|null} - The selected pickup location ID
   */
  static getSelectedPickupLocationId(deliveryZoneResponse, selectedPickupLocationId) {
    const pickupLocationsExist = Boolean(
      deliveryZoneResponse && deliveryZoneResponse.pickup_locations && deliveryZoneResponse.pickup_locations.length > 0
    )

    if (!pickupLocationsExist) {
      return null
    }

    let resultId = selectedPickupLocationId

    if (resultId) {
      const selectedPickupLocationExists = deliveryZoneResponse.pickup_locations.find(
        (location) => location.id == resultId
      )

      if (!selectedPickupLocationExists) {
        resultId = null
      }
    }

    return resultId ? resultId : deliveryZoneResponse.pickup_locations[0].id
  }

  /**
   * Renders a pickup location element
   * @param {Object} location - The pickup location data
   * @param {HTMLTemplateElement} pickupLocationTemplate - The template for pickup locations
   * @returns {DocumentFragment} - The rendered pickup location
   */
  static renderPickupLocation(location, pickupLocationTemplate) {
    const template = pickupLocationTemplate.content.cloneNode(true)

    const pickupLocation = modifyHTML(template, (htmlString) => {
      htmlString = htmlString.replaceAll("[[ store_id ]]", location.id)
      htmlString = htmlString.replaceAll("[[ store_name ]]", location.friendlyName)
      htmlString = htmlString.replaceAll("[[ store_address1 ]]", location.address.address)
      htmlString = htmlString.replaceAll("[[ store_distance ]]", `${location.distanceMi.toFixed(1)} mi`)

      return htmlString
    })

    return pickupLocation
  }

  /**
   * Populates a form with address data
   * @param {HTMLFormElement} form - The form to populate
   * @param {Object} parsedAddress - The parsed address data
   * @returns {Object} - The form data as an object
   */
  static populateForm(form, parsedAddress) {
    const { city, province, address1, postcode, country, lat, lng, place_id } = parsedAddress

    form.querySelector("[name=city]").value = city || ""
    form.querySelector("[name=province]").value = province || ""
    form.querySelector("[name=address1]").value = address1 || ""
    form.querySelector("[name=postalCode]").value = postcode || ""
    form.querySelector("[name=country]").value = country || ""
    form.querySelector("[name=latitude]").value = lat || ""
    form.querySelector("[name=longitude]").value = lng || ""
    form.querySelector("[name=place_id]").value = place_id || ""

    const formData = new FormData(form)
    const formDataObject = Object.fromEntries(formData)
    delete formDataObject.selected_delivery_type

    return formDataObject
  }

  /**
   * Clears place and address data from a form
   * @param {HTMLFormElement} form - The form to clear
   * @param {HTMLInputElement} autocompleteInput - The autocomplete input field
   */
  static clearPlaceData(form, autocompleteInput) {
    autocompleteInput.value = ""
    form.querySelector("[name=city]").value = ""
    form.querySelector("[name=province]").value = ""
    form.querySelector("[name=address1]").value = ""
    form.querySelector("[name=postalCode]").value = ""
    form.querySelector("[name=country]").value = ""
    form.querySelector("[name=latitude]").value = ""
    form.querySelector("[name=longitude]").value = ""
    form.querySelector("[name=place_id]").value = ""
  }
}
